import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useLiterals from 'utils/hooks/useLiterals';
import Box from 'components/UI/Box/Box';
import Button from 'components/UI/Button';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import './styles.scss';

const CardSelector = ({
  cards = [],
  onCardSelect = () => {},
  onCardEdit = () => {},
}) => {
  const literals = useLiterals('mercadopago');
  const [selectedCardId, setSelectedCardId] = useState(null);

  const handleCardCheck = (cardId) => {
    if (selectedCardId === cardId) {
      setSelectedCardId(null);
    } else {
      setSelectedCardId(cardId);
    }
  };

  const handleSelectCard = () => {
    if (!selectedCardId) {
      return;
    }

    const selectedCard = cards.find((card, index) => {
      const cardId = card.id || `card-${index}`;
      return cardId === selectedCardId;
    });

    if (selectedCard && !selectedCard.disabled) {
      const cardId = selectedCard.id || `card-${cards.findIndex((c) => c === selectedCard)}`;
      onCardSelect({ ...selectedCard, id: cardId });
    }
  };

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className='card-selector'>
      <h4 className='mb-4'>{literals.cardSelector?.title || 'Selecciona una tarjeta'}</h4>
      <div className='cards-list'>
        {cards.map((card, index) => {
          const cardId = card.id || `card-${index}`;
          const isSelected = selectedCardId === cardId;
          const isDisabled = card.disabled || false;
          const thumbnail = card.payment_method?.thumbnail;

          return (
            <Box
              key={cardId}
              className={classNames('card-item', {
                selected: isSelected,
                disabled: isDisabled,
              })}
            >
              <div className='card-item-content'>
                <div className='flex items-center gap-4'>
                  <input
                    type='checkbox'
                    checked={isSelected}
                    onChange={() => !isDisabled && handleCardCheck(cardId)}
                    disabled={isDisabled}
                    className='form-check-input'
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      accentColor: 'var(--primary-color)',
                    }}
                  />
                  <div className='grow'>
                    <div className='card-item-header'>
                      {thumbnail && (
                        <img
                          src={thumbnail}
                          alt={card.payment_method?.name || 'Tarjeta'}
                          className='card-brand-logo'
                        />
                      )}
                      <div className='card-item-info'>
                        <h5 className='mb-1'>{card.payment_method?.name || literals.cardSelector?.defaultCardName || 'Tarjeta'}</h5>
                        <p className='text-neutral-500 mb-0'>
                          ••••
                          {' '}
                          {card.last_four_digits}
                        </p>
                      </div>
                    </div>
                    {card.cardholder?.name && (
                      <p className='card-holder-name mb-0'>
                        {card.cardholder.name}
                      </p>
                    )}
                    <p className='card-expiration mb-0'>
                      {String(card.expiration_month).padStart(2, '0')}
                      /
                      {card.expiration_year}
                    </p>
                  </div>
                  {!isDisabled && (
                    <button
                      type='button'
                      className='card-edit-button'
                      onClick={(e) => {
                        e.stopPropagation();
                        onCardEdit(card);
                      }}
                      aria-label={literals.cardSelector?.editButton || 'Editar tarjeta'}
                      title={literals.cardSelector?.editButton || 'Editar tarjeta'}
                    >
                      <PencilIcon />
                    </button>
                  )}
                </div>
                {isDisabled && (
                  <span className='card-disabled-badge'>{literals.cardSelector?.invalidCard || 'Tarjeta inválida'}</span>
                )}
              </div>
            </Box>
          );
        })}
      </div>
      <div className='mt-4'>
        <Button
          text={literals.cardSelector?.selectButton}
          type='primary'
          className='w-full'
          onClick={handleSelectCard}
          disabled={!selectedCardId}
        />
      </div>
    </div>
  );
};

CardSelector.propTypes = {
  cards: PropTypes.array.isRequired,
  onCardSelect: PropTypes.func.isRequired,
  onCardEdit: PropTypes.func,
};

export default CardSelector;
